import { authManagerInstance, AuthProvider, ProviderAuthStatus } from "../values";
import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";

export const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_CLIENT_ID,
        authority: 'https://login.microsoftonline.com/common/',
        redirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => { },
            piiLoggingEnabled: false
        }
    }
};

export const msalInstance = new PublicClientApplication(msalConfig);
(async () => {
    try {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
    } catch (error) { }
})();

export const msalScopes = {
    basic: ['User.Read'],
    mail: ['Mail.Read'],
    calendar: ['Calendars.Read'],
    files: ['Files.Read'],
    all: ['Mail.Read', 'User.Read', 'Calendars.Read', 'Files.Read']
};

class MicrosoftAuthService {
    constructor() {
        this.msalInstance = null;
        this.initialized = false;
        this.initPromise = null;
        this.accountChangeListeners = new Set();
    }

    async initialize() {
        if (this.initialized) return;
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = new Promise(async (resolve, reject) => {
            try {
                this.msalInstance = new PublicClientApplication(msalConfig);
                await this.msalInstance.initialize();
                this.msalInstance.addEventCallback((event) => {
                    if (event.eventType === 'loginSuccess' ||
                        event.eventType === 'acquireTokenSuccess' ||
                        event.eventType === 'logoutSuccess') {
                        this.notifyAccountChange();
                    }
                });
                try {
                    const response = await this.msalInstance.handleRedirectPromise();
                    if (response) {
                        await this.handleAuthResponse(response);
                    }
                }
                catch (redirectError) { }
                await this.syncAccounts();
                this.initialized = true;
                resolve();
            } catch (error) {
                reject(error);
            }
        });

        return this.initPromise;
    }

    async syncAccounts() {
        this.restoreActiveAccounts();
        await this.ensureMsalAccountsLoaded();
    }

    restoreActiveAccounts() {
        try {
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                accounts.forEach(msalAccount => {
                    const existingAccounts = authManagerInstance.getAccounts(AuthProvider.MICROSOFT);
                    const matchingAccount = existingAccounts.find(acc =>
                        acc.userData &&
                        (acc.userData.username === msalAccount.username ||
                            acc.userData.localAccountId === msalAccount.localAccountId)
                    );
                    if (!matchingAccount) {
                        const userData = {
                            provider: AuthProvider.MICROSOFT,
                            name: msalAccount.name,
                            username: msalAccount.username,
                            email: msalAccount.username,
                            localAccountId: msalAccount.localAccountId,
                            homeAccountId: msalAccount.homeAccountId,
                            environment: msalAccount.environment,
                            tenantId: msalAccount.tenantId,
                        };
                        try {
                            authManagerInstance.addAccount(AuthProvider.MICROSOFT, userData);
                        } catch (error) {
                            if (!error.message.includes("Account limit reached")) { }
                        }
                    }
                });

                if (accounts.length > 0) {
                    this.msalInstance.setActiveAccount(accounts[0]);
                }
            }
        }
        catch (error) { }
    }

    async handleAuthResponse(response) {
        if (!response || !response.account) return null;
        const account = response.account;
        const userData = {
            provider: AuthProvider.MICROSOFT,
            name: account.name,
            username: account.username,
            email: account.username,
            accessToken: response.accessToken,
            tokenExpiry: response.expiresOn.getTime(),
            idTokenClaims: account.idTokenClaims,
            localAccountId: account.localAccountId,
            homeAccountId: account.homeAccountId,
            environment: account.environment,
            tenantId: account.tenantId || account.homeAccountId.split('.')[1]
        };

        try {//eslint-disable-next-line
            const accountRecord = authManagerInstance.addAccount(AuthProvider.MICROSOFT, userData);
            this.msalInstance.setActiveAccount(account);
            return userData;
        } catch (error) {
            if (error.message && error.message.includes("Account limit reached")) {
                throw error;
            }
            return null;
        }
    }

    async ensureMsalAccountsLoaded() {
        try {
            const microsoftAccounts = authManagerInstance.getAccounts(AuthProvider.MICROSOFT);
            const msalAccounts = this.msalInstance.getAllAccounts();
            for (const account of microsoftAccounts) {
                if (!account.userData) continue;
                let msalAccountExists = false;

                if (account.userData.username) {
                    msalAccountExists = msalAccounts.some(acc => acc.username === account.userData.username);
                }

                if (!msalAccountExists && account.userData.localAccountId) {
                    msalAccountExists = msalAccounts.some(acc => acc.localAccountId === account.userData.localAccountId);
                }

                if (!msalAccountExists && account.userData.homeAccountId) {
                    msalAccountExists = msalAccounts.some(acc => acc.homeAccountId === account.userData.homeAccountId);
                }
                if (!msalAccountExists) {
                    try {
                        const loginRequest = {
                            scopes: msalScopes.basic,
                            loginHint: account.userData.username || account.userData.email,
                            prompt: 'none',
                        };

                        try {
                            await this.msalInstance.ssoSilent(loginRequest);
                        }
                        catch (ssoError) { }
                    }
                    catch (refreshError) { }
                }
            }
        }
        catch (error) { }
    }

    async login(scopes = msalScopes.all) {
        await this.initialize();
        if (!authManagerInstance.canAddMoreAccounts()) {
            const subscriptionInfo = authManagerInstance.getSubscriptionInfo();
            const tierName = subscriptionInfo.name || 'Free';
            throw new Error(`Account limit reached for your ${tierName} plan. Please upgrade to add more accounts.`);
        }

        const loginRequest = {
            scopes,
            prompt: 'select_account',
        };

        try {
            const currentAccounts = this.msalInstance.getAllAccounts();
            if (currentAccounts.length > 0) {
                try {
                    await this.msalInstance.logoutRedirect({
                        onRedirectNavigate: () => false,
                    });
                } catch (silentLogoutError) { }
            }
            const response = await this.msalInstance.loginPopup(loginRequest);
            return await this.handleAuthResponse(response);
        } catch (error) {
            throw error;
        }
    }

    async logout(accountId) {
        await this.initialize();
        const microsoftAccounts = authManagerInstance.getAccounts(AuthProvider.MICROSOFT);
        const account = microsoftAccounts.find(acc => acc.id === accountId);
        if (!account) {
            throw new Error("Invalid Microsoft account");
        }

        let msalAccount = null;
        const msalAccounts = this.msalInstance.getAllAccounts();
        if (account.userData) {
            if (account.userData.username) {
                msalAccount = msalAccounts.find(acc => acc.username === account.userData.username);
            }
            if (!msalAccount && account.userData.localAccountId) {
                msalAccount = msalAccounts.find(acc => acc.localAccountId === account.userData.localAccountId);
            }
            if (!msalAccount && account.userData.homeAccountId) {
                msalAccount = msalAccounts.find(acc => acc.homeAccountId === account.userData.homeAccountId);
            }
        }

        try {
            if (msalAccount) {
                try {
                    await this.msalInstance.getTokenCache().removeAccount(msalAccount);
                } catch (tokenError) { }
                try {
                    const logoutRequest = {
                        account: msalAccount,
                        postLogoutRedirectUri: window.location.origin,
                        mainWindowRedirectUri: window.location.origin
                    };
                    await this.msalInstance.logoutPopup(logoutRequest);
                } catch (logoutError) { }
            }
            authManagerInstance.removeAccount(accountId);
            const remainingMsalAccounts = this.msalInstance.getAllAccounts();
            if (remainingMsalAccounts.length > 0) {
                this.msalInstance.setActiveAccount(remainingMsalAccounts[0]);
            }
            return true;
        } catch (error) {
            authManagerInstance.removeAccount(accountId);
            return false;
        }
    }

    async getAccessToken(accountId, scopes = msalScopes.basic, interactive = false) {
        await this.initialize();
        const cachedToken = authManagerInstance.getAccessToken(accountId);
        const tokenInfo = authManagerInstance.getTokenInfo(accountId);
        if (cachedToken && tokenInfo &&
            tokenInfo.expiresAt > (Date.now() + 10 * 60 * 1000) &&
            tokenInfo.scopes &&
            authManagerInstance.scopesIncluded(tokenInfo.scopes, scopes)) {
            return cachedToken;
        }
        const microsoftAccounts = authManagerInstance.getAccounts(AuthProvider.MICROSOFT);
        const account = microsoftAccounts.find(acc => acc.id === accountId);

        if (!account || !account.userData) {
            throw new Error("Invalid Microsoft account");
        }
        let msalAccount = null;
        let msalAccounts = this.msalInstance.getAllAccounts();
        if (account.userData.username) {
            msalAccount = msalAccounts.find(acc => acc.username === account.userData.username);
        }
        if (!msalAccount && account.userData.localAccountId) {
            msalAccount = msalAccounts.find(acc => acc.localAccountId === account.userData.localAccountId);
        }
        if (!msalAccount && account.userData.homeAccountId) {
            msalAccount = msalAccounts.find(acc => acc.homeAccountId === account.userData.homeAccountId);
        }
        if (!msalAccount) {
            try {
                await this.syncAccounts();
                msalAccounts = this.msalInstance.getAllAccounts();
                if (account.userData.username) {
                    msalAccount = msalAccounts.find(acc => acc.username === account.userData.username);
                }
                if (!msalAccount && account.userData.localAccountId) {
                    msalAccount = msalAccounts.find(acc => acc.localAccountId === account.userData.localAccountId);
                }
                if (!msalAccount && account.userData.homeAccountId) {
                    msalAccount = msalAccounts.find(acc => acc.homeAccountId === account.userData.homeAccountId);
                }
                if (!msalAccount && account.userData.username) {
                    try {
                        const loginRequest = {
                            scopes: scopes,
                            loginHint: account.userData.username,
                            prompt: 'none',
                        };
                        const response = await this.msalInstance.ssoSilent(loginRequest);
                        msalAccount = response.account;
                        if (msalAccount && accountId) {
                            const userData = {
                                ...account.userData,
                                homeAccountId: msalAccount.homeAccountId,
                                localAccountId: msalAccount.localAccountId,
                                environment: msalAccount.environment,
                            };
                            authManagerInstance.updateUserData(accountId, userData);
                        }
                    } catch (ssoError) {
                        if (!interactive) {
                            throw new Error("Session expired. Please login again.");
                        }
                    }
                } else if (!interactive) {
                    throw new Error("Session expired. Please login again.");
                }
            } catch (error) {
                if (!interactive) {
                    throw new Error(`MSAL account not found: ${error.message}. Please try logging in again.`);
                }
            }
        }
        try {
            if (msalAccount && !interactive) {
                this.msalInstance.setActiveAccount(msalAccount);
                const silentRequest = {
                    account: msalAccount,
                    scopes,
                    forceRefresh: tokenInfo ? true : false,
                };
                try {
                    const response = await this.msalInstance.acquireTokenSilent(silentRequest);
                    authManagerInstance.setAccessToken(
                        accountId,
                        response.accessToken,
                        (response.expiresOn.getTime() - Date.now()) / 1000,
                        scopes
                    );
                    return response.accessToken;
                } catch (silentError) {
                    if (silentError instanceof InteractionRequiredAuthError) {
                        if (interactive) {
                            const response = await this.msalInstance.acquireTokenPopup({
                                ...silentRequest,
                                prompt: 'select_account',
                            });
                            authManagerInstance.setAccessToken(
                                accountId,
                                response.accessToken,
                                (response.expiresOn.getTime() - Date.now()) / 1000,
                                scopes,
                            );
                            return response.accessToken;
                        } else {
                            throw new Error("Interactive authentication required. Please try again.");
                        }
                    } else {
                        throw silentError;
                    }
                }
            }
            if (interactive) {
                const request = msalAccount
                    ? {
                        scopes,
                        account: msalAccount,
                        prompt: 'select_account',
                    } : {
                        scopes,
                        loginHint: account.userData.username,
                        prompt: 'select_account',
                    };
                const response = await this.msalInstance.acquireTokenPopup(request);
                authManagerInstance.setAccessToken(
                    accountId,
                    response.accessToken,
                    (response.expiresOn.getTime() - Date.now()) / 1000,
                    scopes
                );
                return response.accessToken;
            } else {
                throw new Error("Failed to acquire token silently and interactive acquisition not requested");
            }
        } catch (error) {
            throw error;
        }
    }

    scopesIncluded(existingScopes, requiredScopes) {
        if (!existingScopes || !requiredScopes) return false;
        const existingScopesArray = Array.isArray(existingScopes) ? existingScopes : [existingScopes];
        const requiredScopesArray = Array.isArray(requiredScopes) ? requiredScopes : [requiredScopes];
        return requiredScopesArray.every(scope =>
            existingScopesArray.some(existingScope =>
                existingScope.toLowerCase() === scope.toLowerCase() ||
                existingScope.toLowerCase() === 'mail.readwrite.all' ||
                existingScope.toLowerCase() === 'mail.send.all',
            )
        );
    }

    getAllAccounts() {
        if (!this.initialized || !this.msalInstance) {
            return [];
        }
        return this.msalInstance.getAllAccounts();
    }

    addAccountChangeListener(callback) {
        if (typeof callback === 'function') {
            this.accountChangeListeners.add(callback);
            return true;
        }
        return false;
    }

    removeAccountChangeListener(callback) {
        return this.accountChangeListeners.delete(callback);
    }

    notifyAccountChange() {
        this.accountChangeListeners.forEach(callback => {
            try {
                callback();
            } catch (error) { }
        });
    }
}

export const microsoftAuthService = new MicrosoftAuthService();
export const handleLogin = async (setUser, setLoading, setError, setIsMicrosoftLoggedIn) => {
    try {
        setLoading(true);
        const userData = await microsoftAuthService.login();
        if (userData) {
            setUser(userData);
            setIsMicrosoftLoggedIn(ProviderAuthStatus.LOGGED_IN);
            return userData;
        }
        return null;
    } catch (err) {
        if (!err.message || !err.message.includes("Account limit reached")) {
            setError(`Login failed: ${err.message}`);
        } else {
            setError(err.message);
        }
        return null;
    } finally {
        setLoading(false);
    }
};

export const handleLogout = async (accountId, setUser, setLoading, setError, setIsMicrosoftLoggedIn) => {
    try {
        setLoading(true);
        await microsoftAuthService.logout(accountId);
        const remainingAccounts = authManagerInstance.getAccounts(AuthProvider.MICROSOFT);
        if (remainingAccounts.length === 0) {
            setIsMicrosoftLoggedIn(ProviderAuthStatus.NOT_LOGGED_IN);
            setUser(null);
        } else {
            setUser(remainingAccounts[0].userData);
        }
        return true;
    } catch (err) {
        setError(`Logout failed: ${err.message}`);
        return false;
    } finally {
        setLoading(false);
    }
};

microsoftAuthService.initialize().catch(error => { });